import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Coffee, Sun, Sunset, Moon } from "lucide-react";
import { TRY, NUM } from "@/lib/api";
import { Card } from "@/components/Cards";

const META = {
  "Kahvaltı": { icon: Coffee, color: "#FBBF24", text: "text-amber-400", bg: "bg-amber-400/10", short: "Kahvaltı" },
  "Öğle Yemeği": { icon: Sun, color: "#E27D38", text: "text-primary", bg: "bg-primary/10", short: "Öğle" },
  "Akşam Yemeği": { icon: Sunset, color: "#38BDF8", text: "text-sky-400", bg: "bg-sky-400/10", short: "Akşam" },
  "Gece Vardiyası": { icon: Moon, color: "#A855F7", text: "text-purple-400", bg: "bg-purple-400/10", short: "Gece" },
};

export default function MealBreakdown({ data }) {
  if (!data) return null;
  const trend = data.trend.map((t) => ({ ...t, gun: t.date.slice(8) + "." + t.date.slice(5, 7) }));
  return (
    <Card className="p-5" data-testid="meal-breakdown-card">
      <h3 className="font-heading text-lg font-semibold mb-1">Öğün Bazlı Kırılım</h3>
      <p className="text-xs text-muted-foreground mb-4">Öğle / Akşam / Gece vardiyası — bugün ve bu ay</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {data.breakdown.map((b) => {
          const m = META[b.meal] || META["Öğle Yemeği"];
          const Icon = m.icon;
          return (
            <div key={b.meal} data-testid={`meal-card-${m.short}`} className="rounded-xl border border-border bg-muted/30 p-4">
              <div className="flex items-center gap-2">
                <div className={`h-8 w-8 rounded-lg ${m.bg} ${m.text} flex items-center justify-center`}><Icon className="h-4 w-4" /></div>
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{b.meal}</span>
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <span className="text-[11px] text-muted-foreground">Bugün</span>
                <span className="font-mono font-bold">{NUM(b.daily_headcount)} kişi</span>
              </div>
              <div className={`font-mono text-sm ${m.text} text-right`}>{TRY(b.daily_revenue)}</div>
              <div className="mt-2 pt-2 border-t border-border flex items-baseline justify-between">
                <span className="text-[11px] text-muted-foreground">Bu ay</span>
                <span className="font-mono text-sm font-semibold">{NUM(b.month_headcount)} kişi · {TRY(b.month_revenue)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={trend} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="gun" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
          <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <Tooltip formatter={(v) => `${NUM(v)} kişi`} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {data.meals.map((m) => <Bar key={m} dataKey={m} stackId="m" fill={META[m]?.color || "#999"} />)}
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}
